import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { Logo } from '@/components/brand/logo';
import { useFeatures, type Feature } from '@/lib/features';
import {
  BellRing,
  CalendarDays,
  FileSignature,
  PiggyBank,
  Boxes,
  ClipboardList,
  Eye,
  FileText,
  LayoutDashboard,
  LogOut,
  Menu,
  Sparkles,
  Settings,
  Tag,
  Users,
  Workflow,
  X,
  Zap,
  FileClock,
  ReceiptText,
  ShoppingCart,
  Truck,
} from 'lucide-react';
import type { ParseKeys } from 'i18next';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/theme-toggle';
import { BillingBanner } from '@/components/billing-banner';
import { useAuth } from '@/lib/auth';
import { useAnalytics } from '@/lib/analytics';
import { AdminNavLink } from '@/features/admin/admin-nav-link';
import { AssistantLauncher } from '@/features/assistant/assistant-launcher';
import { cn, initials } from '@/lib/utils';
import type { UserRole } from '@/lib/schemas';

interface NavItem {
  to: string;
  labelKey: ParseKeys<'common'>;
  icon: typeof LayoutDashboard;
  /** מי רואה את הפריט. ההרשאה עצמה נאכפת בשרת — כאן רק הסתרה. */
  roles?: UserRole[];
  /** המודול שהפריט שייך אליו (lib/features.ts). כבוי לעסק — הפריט לא מוצג. */
  feature?: Feature;
}

/* סרגל הצד בנייבי של המותג (בכהה — עמוק מהקנבס). הלוגו עובר ללבן בתוכו:
   דורסים את טוקן הדיו רק בתוך הסרגל, בלי גרסת לוגו נפרדת. */
const SIDEBAR =
  'border-e border-nav-border bg-nav text-nav-fg [--color-brand-ink:var(--color-nav-fg)] [--color-brand-bubble:var(--color-accent-bright)]';
/* כפתורי ghost בתוך הסרגל — הגוונים של הסרגל, לא של הקנבס. */
const NAV_GHOST = 'text-nav-fg-muted hover:bg-nav-hover hover:text-nav-fg focus-visible:outline-accent-bright';

/** אדמין, עריכה וצפייה בלבד. לצופה אין כפתורי כתיבה — המסכים עצמם מסתירים (useCanEdit). */
const READERS: UserRole[] = ['OWNER', 'MANAGER', 'VIEWER'];

const NAV: NavItem[] = [
  // התפקידים כאן תואמים ל-@Roles בשרת ול-RequireRole במסלול: תפריט שמציג
  // מסך שהשרת יחסום הוא הבטחה שבורה. טכנאי לא רואה כלום — הוא מועבר
  // לאפליקציית השטח (ראו components/require-role.tsx). ההגדרות פתוחות
  // לכולם, אבל מי שאינו אדמין רואה שם רק את הפרופיל שלו.
  { to: '/', labelKey: 'nav.overview', icon: LayoutDashboard, roles: READERS },
  { to: '/assistant', labelKey: 'nav.assistant', icon: Sparkles, roles: READERS },
  { to: '/tasks', labelKey: 'nav.tasks', icon: ClipboardList, roles: READERS },
  { to: '/schedule', labelKey: 'nav.schedule', icon: CalendarDays, roles: READERS, feature: 'scheduling' },
  { to: '/customers', labelKey: 'nav.customers', icon: Users, roles: READERS, feature: 'customers' },
  { to: '/quotes', labelKey: 'nav.quotes', icon: FileSignature, roles: READERS, feature: 'quotes' },
  { to: '/maintenance', labelKey: 'nav.maintenance', icon: BellRing, roles: READERS, feature: 'equipment' },
  { to: '/contracts', labelKey: 'nav.contracts', icon: FileClock, roles: READERS, feature: 'contracts' },
  { to: '/suppliers', labelKey: 'nav.suppliers', icon: Truck, roles: READERS, feature: 'suppliers' },
  { to: '/purchase-orders', labelKey: 'nav.purchaseOrders', icon: ShoppingCart, roles: READERS, feature: 'purchaseOrders' },
  { to: '/supplier-invoices', labelKey: 'nav.supplierInvoices', icon: ReceiptText, roles: READERS, feature: 'supplierInvoices' },
  { to: '/inventory', labelKey: 'nav.inventory', icon: Boxes, roles: READERS, feature: 'inventory' },
  { to: '/price-list', labelKey: 'nav.priceList', icon: Tag, roles: READERS, feature: 'priceList' },
  { to: '/invoices', labelKey: 'nav.invoices', icon: FileText, roles: READERS, feature: 'invoicing' },
  { to: '/profitability', labelKey: 'nav.profitability', icon: PiggyBank, roles: READERS, feature: 'invoicing' },
  { to: '/processes', labelKey: 'nav.processes', icon: Workflow, roles: READERS },
  { to: '/workflows', labelKey: 'nav.workflows', icon: Zap, roles: READERS },
  { to: '/settings', labelKey: 'nav.settings', icon: Settings, roles: READERS },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { session, status, logout, can } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  // סיום סשן — התנתקות, או 401 מכל קריאה — מעביר מיד להתחברות. ה-guard של
  // המסלול רץ רק בניווט הבא, ובלי זה המשתמש נשאר על מסך שכל בקשה בו נכשלת
  // (כמו ב-AppShell של אפליקציית הטכנאי).
  useEffect(() => {
    if (status === 'anonymous') void navigate({ to: '/login', replace: true });
  }, [status, navigate]);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // אנליטיקת שימוש first-party (lib/analytics.ts) — רק במסכים המחוברים.
  useAnalytics(status === 'authenticated');
  const isAdmin = session?.isPlatformAdmin === true;

  const features = useFeatures();
  const visible = NAV.filter((n) => (!n.roles || can(...n.roles)) && (!n.feature || features.has(n.feature)));

  return (
    <div className="min-h-dvh bg-canvas">
      {/* דילוג לתוכן — הדבר הראשון ב-tab order. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-3 focus:rounded-(--radius-md) focus:bg-accent focus:px-4 focus:py-2 focus:text-fg-on-accent"
      >
        {t('layout.skipToContent')}
      </a>

      {/* ---- סרגל צד, דסקטופ. ב-RTL הוא בצד ימין; `start` מטפל בזה. ---- */}
      <aside className={cn(SIDEBAR, 'fixed inset-y-0 start-0 z-30 hidden w-60 flex-col lg:flex')}>
        <Brand />
        <Nav items={visible} pathname={pathname} isAdmin={isAdmin} />
        <UserFooter name={session?.user.name ?? ''} role={session?.user.role} onLogout={logout} />
      </aside>

      {/* ---- מגירה, מובייל ---- */}
      {mobileOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            aria-label={t('layout.closeMenu')}
            onClick={() => setMobileOpen(false)}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />
          <aside className={cn(SIDEBAR, 'absolute inset-y-0 start-0 flex w-64 flex-col shadow-lg')}>
            <div className="flex items-center justify-between pe-2">
              <Brand />
              <Button
                variant="ghost"
                size="icon"
                className={NAV_GHOST}
                onClick={() => setMobileOpen(false)}
                aria-label={t('actions.close')}
              >
                <X aria-hidden />
              </Button>
            </div>
            <Nav items={visible} pathname={pathname} isAdmin={isAdmin} onNavigate={() => setMobileOpen(false)} />
            <UserFooter name={session?.user.name ?? ''} role={session?.user.role} onLogout={logout} />
          </aside>
        </div>
      ) : null}

      {/* ---- אזור התוכן ---- */}
      <div className="lg:ps-60">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-border bg-surface/85 px-4 shadow-xs backdrop-blur-md">
          {/* פס המותג בתחתית הכותרת — הצבע של המוצר בכל מסך, בלי לגעת בתוכן. */}
          <span className="bg-brand-gradient pointer-events-none absolute inset-x-0 -bottom-px h-0.5" aria-hidden />
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label={t('layout.openMenu')}
          >
            <Menu aria-hidden />
          </Button>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-fg">{session?.tenant.name}</p>
          </div>
          {/* צפייה בלבד: מסביר למה אין כפתורי עריכה באף מסך. */}
          {session?.user.role === 'VIEWER' ? (
            <Badge tone="info" dot={false} title={t('layout.viewOnlyHint')}>
              <Eye className="size-3" aria-hidden />
              {t('layout.viewOnly')}
              <span className="sr-only">{` — ${t('layout.viewOnlyHint')}`}</span>
            </Badge>
          ) : null}
          <ThemeToggle />
        </header>

        {/* מצב המנוי (ניסיון שנגמר, תשלום שנכשל, קריאה בלבד) — מעל כל מסך. */}
        <BillingBanner />

        <main id="main" className="p-4 lg:p-6">
          {children}
        </main>
      </div>

      {/* העוזר: כפתור צף ומגירה בכל מסך מחובר. AppShell לא עוטף דפים ציבוריים. */}
      <AssistantLauncher />
    </div>
  );
}

function Brand() {
  return (
    <div className="flex h-14 items-center gap-2.5 px-5">
      <Logo />
    </div>
  );
}

function Nav({
  items,
  pathname,
  isAdmin = false,
  onNavigate,
}: {
  items: NavItem[];
  pathname: string;
  isAdmin?: boolean;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <nav className="flex-1 space-y-0.5 overflow-y-auto p-3" aria-label={t('layout.mainNav')}>
      {items.map(({ to, labelKey, icon: Icon }) => {
        const active = to === '/' ? pathname === '/' : pathname.startsWith(to);
        return (
          <Link
            key={to}
            to={to}
            onClick={onNavigate}
            data-track={`nav.${to === '/' ? 'overview' : to.slice(1)}`}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'relative flex items-center gap-3 rounded-(--radius-md) px-3 py-2.5 text-sm transition-colors duration-(--duration-fast)',
              'focus-visible:outline-accent-bright',
              active
                ? 'bg-nav-active font-semibold text-nav-active-fg shadow-sm'
                : 'text-nav-fg-muted hover:bg-nav-hover hover:text-nav-fg',
            )}
          >
            {/* פס התכלת של הלוגו בצד ה-start של הפריט הפעיל — צורה, לא רק צבע. */}
            {active ? (
              <span className="absolute inset-y-2 -start-3 w-1 rounded-e-full bg-accent-bright" aria-hidden />
            ) : null}
            <Icon className={cn('size-4 shrink-0', !active && 'text-nav-fg-muted')} aria-hidden />
            {t(labelKey)}
          </Link>
        );
      })}
      {isAdmin ? <AdminNavLink pathname={pathname} onNavigate={onNavigate} /> : null}
    </nav>
  );
}

const ROLE_LABEL = {
  OWNER: 'roles.OWNER',
  MANAGER: 'roles.MANAGER',
  VIEWER: 'roles.VIEWER',
  FIELD: 'roles.FIELD',
} as const satisfies Record<UserRole, ParseKeys<'common'>>;

function UserFooter({
  name,
  role,
  onLogout,
}: {
  name: string;
  role?: UserRole;
  onLogout: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2.5 border-t border-nav-border p-3">
      <div
        className="grid size-8 shrink-0 place-items-center rounded-full bg-accent-bright text-2xs font-bold text-nav"
        aria-hidden
      >
        {initials(name)}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-nav-fg">{name}</p>
        {role ? <p className="truncate text-2xs text-nav-fg-muted">{t(ROLE_LABEL[role])}</p> : null}
      </div>
      <Button variant="ghost" size="icon" onClick={onLogout} aria-label={t('layout.logout')} className={cn(NAV_GHOST, 'size-8')}>
        <LogOut className="size-3.5" aria-hidden />
      </Button>
    </div>
  );
}
